import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const COLORS = {
  text: '#111111',
  subtext: '#555555',
  border: '#D9D9D9',
  lightBorder: '#EDEDED',
  headerBg: '#F4F4F4',
  tableHeaderBg: '#EDEDED',
  zebra: '#FAFAFA',
  black: '#000000',
  white: '#FFFFFF',
  grayDark: '#222222',
  grayMid: '#666666',
};

const FONT_SIZES = {
  title: 16,
  h1: 13,
  h2: 11,
  body: 9,
  small: 8,
};

const FIRST_PAGE_MAX_ITEM_ROWS = 10;

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: COLORS.white,
    padding: 20,
    fontSize: FONT_SIZES.body,
    fontFamily: 'Helvetica',
    color: COLORS.text,
  },
  quotationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    backgroundColor: COLORS.headerBg,
    border: `1px solid ${COLORS.border}`,
    padding: 12,
    marginBottom: 16,
  },
  companySection: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    flex: 1,
    gap: 10,
  },
  companyLogo: {
    width: 80,
    height: 80,
  },
  companyInfo: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    flex: 1,
  },
  companyName: {
    fontSize: FONT_SIZES.title,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 4,
    color: COLORS.grayDark,
  },
  companyAddress: {
    fontSize: FONT_SIZES.body,
    color: COLORS.subtext,
    lineHeight: 1.4,
  },
  quotationInfoSection: {
    width: 220,
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  taxQuotationBadge: {
    backgroundColor: COLORS.black,
    color: COLORS.white,
    padding: 6,
    fontSize: FONT_SIZES.body,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
    width: 120,
  },
  originalCopyText: {
    border: `1px solid ${COLORS.border}`,
    padding: 4,
    textAlign: 'center',
    fontSize: FONT_SIZES.small,
    color: COLORS.grayMid,
    width: 140,
    marginTop: 6,
  },
  quotationMeta: {
    marginTop: 8,
    width: 200,
  },
  metaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 2,
    borderBottom: `1px solid ${COLORS.lightBorder}`,
  },
  metaLabel: {
    fontSize: FONT_SIZES.small,
    color: COLORS.subtext,
  },
  metaValue: {
    fontSize: FONT_SIZES.body,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.text,
  },
  threeColumnSection: {
    flexDirection: 'row',
    border: `1px solid ${COLORS.border}`,
    marginBottom: 16,
  },
  column: {
    flex: 1,
    padding: 12,
    borderRight: `1px solid ${COLORS.border}`,
  },
  lastColumn: {
    flex: 1,
    padding: 12,
  },
  sectionHeader: {
    fontFamily: 'Helvetica-Bold',
    fontSize: FONT_SIZES.h2,
    color: COLORS.grayDark,
    marginBottom: 8,
    borderBottom: `1px solid ${COLORS.lightBorder}`,
    paddingBottom: 4,
    textTransform: 'uppercase',
  },
   detailItem: {
    flexDirection: 'row',
    marginBottom: 4,
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    overflow: 'hidden',
  },
  detailLabel: {
    fontFamily: 'Helvetica-Bold',
    fontSize: FONT_SIZES.small,
    color: COLORS.subtext,
    width: 70,
    flexShrink: 0,
  },
  detailValue: {
    fontSize: FONT_SIZES.body,
    color: COLORS.text,
    flex: 1,
    flexWrap: 'wrap',
    wordBreak: 'break-all',
  },
  table: {
    width: '100%',
    border: `1px solid ${COLORS.border}`,
    marginBottom: 16,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: COLORS.tableHeaderBg,
    borderBottom: `1px solid ${COLORS.border}`,
  },
  tableHeaderCell: {
    padding: 8,
    fontFamily: 'Helvetica-Bold',
    fontSize: FONT_SIZES.body,
    color: COLORS.grayDark,
    textAlign: 'center',
    borderRight: `1px solid ${COLORS.border}`,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: `1px solid ${COLORS.lightBorder}`,
    minHeight: 24,
  },
  tableRowEven: {
    backgroundColor: COLORS.zebra,
  },
 tableCell: {
    padding: 6,
    fontSize: FONT_SIZES.body,
    textAlign: 'center',
    borderRight: `1px solid ${COLORS.border}`,
    justifyContent: 'center',
    wordWrap: 'break-word',
    overflow: 'hidden',
  },
  tableCellLeft: {
    textAlign: 'left',
  },
  tableCellRight: {
    textAlign: 'right',
  },
  productName: {
    fontFamily: 'Helvetica-Bold',
    fontSize: FONT_SIZES.body,
    marginBottom: 2,
    color: COLORS.grayDark,
  },
  productDesc: {
    fontSize: FONT_SIZES.small,
    color: COLORS.subtext,
    lineHeight: 1.2,
  },
  totalRow: {
    backgroundColor: COLORS.tableHeaderBg,
  },
  totalRowCell: {
    fontFamily: 'Helvetica-Bold',
    fontSize: FONT_SIZES.h2,
    padding: 8,
    textAlign: 'right',
    color: COLORS.grayDark,
  },
  totalWords: {
    border: `1px solid ${COLORS.border}`,
    backgroundColor: COLORS.white,
    padding: 10,
    marginBottom: 12,
  },
  wordsLabel: {
    fontSize: FONT_SIZES.body,
    color: COLORS.text,
  },
  bottomSection: {
    flexDirection: 'row',
    gap: 12,
  },
  leftColumnBottom: {
    flex: 1,
    flexDirection: 'column',
    gap: 10,
  },
  rightColumnBottom: {
    width: 260,
    flexDirection: 'column',
    gap: 10,
  },
  bankCard: {
    border: `1px solid ${COLORS.border}`,
    padding: 12,
    backgroundColor: COLORS.white,
  },
  cardTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: FONT_SIZES.h2,
    color: COLORS.grayDark,
    borderBottom: `1px solid ${COLORS.lightBorder}`,
    paddingBottom: 4,
    marginBottom: 8,
  },
  bankRow: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  bankLabel: {
    fontSize: FONT_SIZES.small,
    color: COLORS.subtext,
    width: 65,
  },
  bankValue: {
    fontSize: FONT_SIZES.body,
    color: COLORS.text,
    flex: 1,
  },
  bankGrid: {
    flexDirection: 'row',
    gap: 12,
  },
  bankColLeft: {
    flex: 1,
  },
  bankColRight: {
    width: 110,
    alignItems: 'center',
  },
  bankQrWrap: {
    marginTop: 0,
    alignItems: 'center',
  },
  qrImage: {
    width: 90,
    height: 90,
    border: `1px solid ${COLORS.border}`,
  },
  termsCard: {
    border: `1px solid ${COLORS.border}`,
    padding: 12,
    backgroundColor: COLORS.white,
  },
  termsListItem: {
    fontSize: FONT_SIZES.body,
    color: COLORS.text,
    marginBottom: 3,
  },
  summaryCard: {
    border: `1px solid ${COLORS.border}`,
    backgroundColor: COLORS.white,
    padding: 12,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 4,
    borderBottom: `1px solid ${COLORS.lightBorder}`,
  },
  summaryLabel: {
    fontSize: FONT_SIZES.body,
    color: COLORS.text,
  },
  summaryValue: {
    fontFamily: 'Helvetica-Bold',
    fontSize: FONT_SIZES.body,
    color: COLORS.grayDark,
  },
  finalTotal: {
    backgroundColor: COLORS.black,
    color: COLORS.white,
    marginTop: 8,
    padding: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  finalTotalLabel: {
    fontFamily: 'Helvetica-Bold',
    fontSize: FONT_SIZES.h2,
    color: COLORS.white,
  },
  finalTotalValue: {
    fontFamily: 'Helvetica-Bold',
    fontSize: FONT_SIZES.h2,
    color: COLORS.white,
  },
});

// Sanitize function for base64/data-url QR code.
const sanitizeQr = (src) => {
  if (!src || typeof src !== "string") return null;
  if (src.startsWith("data:image")) return src.trim();
  if (/^[A-Za-z0-9+/=]+$/.test(src)) {
    return "data:image/png;base64," + src;
  }
  return null;
};

// Indian currency number to words converter with paise
const numberToWords = (num) => {
  const ones = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE'];
  const teens = ['TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];
  const tens = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];

  const convertHundreds = (n) => {
    let result = '';
    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + ' HUNDRED ';
      n %= 100;
    }
    if (n >= 20) {
      result += tens[Math.floor(n / 10)] + ' ';
      n %= 10;
    } else if (n >= 10) {
      result += teens[n - 10] + ' ';
      n = 0;
    }
    if (n > 0) {
      result += ones[n] + ' ';
    }
    return result;
  };

  const convertLargeNumber = (n) => {
    if (n === 0) return '';

    let result = '';
    let crores = Math.floor(n / 10000000);
    n %= 10000000;
    let lakhs = Math.floor(n / 100000);
    n %= 100000;
    let thousands = Math.floor(n / 1000);
    n %= 1000;

    if (crores > 0) result += convertHundreds(crores) + 'CRORE ';
    if (lakhs > 0) result += convertHundreds(lakhs) + 'LAKH ';
    if (thousands > 0) result += convertHundreds(thousands) + 'THOUSAND ';
    if (n > 0) result += convertHundreds(n);

    return result;
  };

  const integerPart = Math.floor(num);
  const decimalPart = Math.round((num - integerPart) * 100);

  let result = integerPart === 0 ? 'ZERO' : convertLargeNumber(integerPart).trim();
  result += ' RUPEES';

  if (decimalPart > 0) {
    result += ' AND ' + convertHundreds(decimalPart).trim() + ' PAISE';
  }

  result += ' ONLY';
  return result;
};

const QuotationPDF = React.memo(({
  quotationData,
  items,
  localSummary,
  getLocalItemTotal,
  calculateLocalItemTax,
  showTaxPerItemColumn,
  hasTax,
  hasdiscount,
  discount,
  taxType,
  sgstRate,
  cgstRate,
  igstRate,
  selectedBank,
  qrCodeDataURL,
  showValueColumn,
}) => {
  const { fromInfo, selectedClient, newClient, quotationInfo, termsAndConditions, userProfile } = quotationData;

  const formatIndianDate = (dateString) => {
    if (!dateString) return new Date().toLocaleDateString('en-IN');
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) return date.toLocaleDateString('en-IN');
    
    const parts = (dateString || '').split(/[-/]/);
    if (parts.length === 3) {
      const year = parts[0].length === 4 ? parts[0] : parts[2];
      const month = parts[1];
      const day = parts.length === 4 ? parts[2] : parts[0];
      return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
    }
    return new Date().toLocaleDateString('en-IN');
  };

  // Compute column widths based on scenario
  const getColumnWidths = () => {
    const base = [
      { key: 'sno', width: 7 },
      { key: 'description', width: 36 },
      { key: 'qty', width: 9 },
      { key: 'rate', width: 12 },
      { key: 'total', width: 14 },
    ];

    const opt = [];
    if (showValueColumn) opt.push({ key: 'value', width: 12 });
    if (showTaxPerItemColumn) opt.push({ key: 'tax', width: 10 });

    const used = [...base, ...opt].reduce((sum, c) => sum + c.width, 0);
    let remaining = 100 - used;

    const widths = {};
    base.forEach((c) => {
      if (c.key === 'description' && remaining > 0) widths[c.key] = c.width + Math.floor(remaining * 0.6);
      else if (c.key === 'total' && remaining > 0) widths[c.key] = c.width + Math.ceil(remaining * 0.4);
      else widths[c.key] = c.width;
    });
    opt.forEach((c) => (widths[c.key] = c.width));

    return widths;
  };

  const columnWidths = getColumnWidths();

  const getTotalSpanWidth = () => {
    const totalWidth = Object.values(columnWidths).reduce((sum, w) => sum + w, 0);
    return totalWidth - columnWidths.total;
  };

  const validItems = items.filter((it) => it.product_name);
  const page1Items = validItems.slice(0, FIRST_PAGE_MAX_ITEM_ROWS);
  const remainingItems = validItems.slice(FIRST_PAGE_MAX_ITEM_ROWS);
  const hasSecondPage = remainingItems.length > 0;

  const ItemsTable = React.memo(({ rows, showHeader = true, startIndex = 0 }) => (
    <View style={styles.table}>
      {showHeader && (
        <View style={styles.tableHeader}>
          <Text style={[styles.tableHeaderCell, { width: `${columnWidths.sno}%` }]}>S.No</Text>
          <Text style={[styles.tableHeaderCell, { width: `${columnWidths.description}%` }]}>Name of Product / Service</Text>
          <Text style={[styles.tableHeaderCell, { width: `${columnWidths.qty}%` }]}>Qty</Text>
          <Text style={[styles.tableHeaderCell, { width: `${columnWidths.rate}%` }]}>Rate</Text>
          {showValueColumn && (
            <Text style={[styles.tableHeaderCell, { width: `${columnWidths.value}%` }]}>Value</Text>
          )}
          {showTaxPerItemColumn && (
            <Text style={[styles.tableHeaderCell, { width: `${columnWidths.tax}%` }]}>Tax</Text>
          )}
          <Text style={[styles.tableHeaderCell, { width: `${columnWidths.total}%`, borderRight: 'none' }]}>Total</Text>
        </View>
      )}
      {rows.map((item, index) => {
        const base = getLocalItemTotal(item);
        const itemTax = calculateLocalItemTax(item);
        const qty = typeof item.quantity === 'string' ? (parseFloat(item.quantity) || 0) : (item.quantity || 0);
        const price = typeof item.unit_price === 'string' ? (parseFloat(item.unit_price) || 0) : (item.unit_price || 0);

        return (
          <View key={index} style={[styles.tableRow, index % 2 === 1 ? styles.tableRowEven : null]}>
            <Text style={[styles.tableCell, { width: `${columnWidths.sno}%` }]}>{startIndex + index + 1}</Text>
            <View style={[styles.tableCell, styles.tableCellLeft, { width: `${columnWidths.description}%` }]}>
              <Text style={styles.productName}>{item.product_name}</Text>
              {!!item.description && <Text style={styles.productDesc}>{item.description}</Text>}
            </View>
            <Text style={[styles.tableCell, { width: `${columnWidths.qty}%` }]}>{qty}</Text>
            <Text style={[styles.tableCell, styles.tableCellRight, { width: `${columnWidths.rate}%` }]}>{price.toFixed(2)}</Text>
            {showValueColumn && (
              <Text style={[styles.tableCell, styles.tableCellRight, { width: `${columnWidths.value}%` }]}>{base.toFixed(2)}</Text>
            )}
            {showTaxPerItemColumn && (
              <Text style={[styles.tableCell, styles.tableCellRight, { width: `${columnWidths.tax}%` }]}>{itemTax.toFixed(2)}</Text>
            )}
            <Text style={[styles.tableCell, styles.tableCellRight, { width: `${columnWidths.total}%`, borderRight: 'none' }]}>
              {(base + (showTaxPerItemColumn ? itemTax : 0)).toFixed(2)}
            </Text>
          </View>
        );
      })}
    </View>
  ));

  const BottomBlocks = React.memo(() => (
    <>
      <View style={styles.totalWords}>
        <Text style={styles.wordsLabel}>
          Total in words: {numberToWords(localSummary.total)}
        </Text>
      </View>
      
      <View style={styles.bottomSection}>
        <View style={styles.leftColumnBottom}>
          {selectedBank && (
            <View style={styles.bankCard}>
              <Text style={styles.cardTitle}>Bank Details</Text>
              <View style={styles.bankGrid}>
                <View style={styles.bankColLeft}>
                  <View style={styles.bankRow}>
                    <Text style={styles.bankLabel}>Bank Name:</Text>
                    <Text style={styles.bankValue}>{selectedBank.bank_name}</Text>
                  </View>
                  <View style={styles.bankRow}>
                    <Text style={styles.bankLabel}>A/C Number:</Text>
                    <Text style={styles.bankValue}>{selectedBank.account_number}</Text>
                  </View>
                  <View style={styles.bankRow}>
                    <Text style={styles.bankLabel}>IFSC Code:</Text>
                    <Text style={styles.bankValue}>{selectedBank.ifsc_code}</Text>
                  </View>
                  {!!selectedBank.account_holder_name && (
                    <View style={styles.bankRow}>
                      <Text style={styles.bankLabel}>A/C Holder:</Text>
                      <Text style={styles.bankValue}>{selectedBank.account_holder_name}</Text>
                    </View>
                  )}
                  {!!selectedBank.upi_id && (
                    <View style={styles.bankRow}>
                      <Text style={styles.bankLabel}>UPI ID:</Text>
                      <Text style={styles.bankValue}>{selectedBank.upi_id}</Text>
                    </View>
                  )}
                </View>
                {selectedBank && qrCodeDataURL && (
                  <View style={styles.bankColRight}>
                    <View style={styles.bankQrWrap}>
                      <Image style={styles.qrImage} src={sanitizeQr(qrCodeDataURL)} />
                      <Text style={[styles.bankValue, { textAlign: 'center', marginTop: 4, fontSize: FONT_SIZES.small }]}>
                        Scan to pay via UPI
                      </Text>
                    </View>
                  </View>
                )}
              </View>
            </View>
          )}

          <View style={styles.termsCard}>
            <Text style={styles.cardTitle}>Terms & Conditions</Text>
            {termsAndConditions && termsAndConditions.filter(t => t.trim() !== '').length > 0 ? (
              termsAndConditions
                .filter(t => t.trim() !== '')
                .map((t, i) => <Text key={i} style={styles.termsListItem}>• {t}</Text>)
            ) : (
              <Text style={styles.termsListItem}>• Quote valid for 30 days.</Text>
            )}
          </View>
        </View>

        <View style={styles.rightColumnBottom}>
          <View style={styles.summaryCard}>
            {hasdiscount && localSummary.discountAmount > 0 && (
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Less: Discount ({discount}%)</Text>
                <Text style={styles.summaryValue}>Rs. {localSummary.discountAmount.toFixed(2)}</Text>
              </View>
            )}

            {hasTax && taxType === 'SGST_CGST' && (
              <>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>CGST ({cgstRate}%)</Text>
                  <Text style={styles.summaryValue}>Rs. {localSummary.cgstTotal.toFixed(2)}</Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>SGST ({sgstRate}%)</Text>
                  <Text style={styles.summaryValue}>Rs. {localSummary.sgstTotal.toFixed(2)}</Text>
                </View>
              </>
            )}

            {hasTax && taxType === 'IGST' && (
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>IGST ({igstRate}%)</Text>
                <Text style={styles.summaryValue}>Rs. {localSummary.igstTotal.toFixed(2)}</Text>
              </View>
            )}

            {hasTax && (
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Total Tax</Text>
                <Text style={styles.summaryValue}>Rs. {localSummary.totalTax.toFixed(2)}</Text>
              </View>
            )}

            <View style={styles.finalTotal}>
              <Text style={styles.finalTotalLabel}>Total Amount</Text>
              <Text style={styles.finalTotalValue}>Rs. {localSummary.total.toFixed(2)}</Text>
            </View>
          </View>
        </View>
      </View>
    </>
  ));

  return (
    <Document>
      {/* Page 1 */}
      <Page size="A4" style={styles.page}>
        {/* Quotation header */}
        <View style={styles.quotationHeader}>
          <View style={styles.companySection}>
            {userProfile?.logo_url && <Image style={styles.companyLogo} src={userProfile.logo_url} />}
            <View style={styles.companyInfo}>
              <Text style={styles.companyName}>{fromInfo?.company_name || fromInfo?.name || 'Company Name'}</Text>
              {!!fromInfo?.address && <Text style={styles.companyAddress}>{fromInfo.address}</Text>}
              {!!fromInfo?.phone && <Text style={styles.companyAddress}>Phone: {fromInfo.phone}</Text>}
              {!!(fromInfo?.contact_email || fromInfo?.email) && (
                <Text style={styles.companyAddress}>Email: {fromInfo.contact_email || fromInfo.email}</Text>
              )}
              {!!fromInfo?.gst_number && <Text style={styles.companyAddress}>GSTIN: {fromInfo.gst_number}</Text>}
            </View>
          </View>

          <View style={styles.quotationInfoSection}>
            <Text style={styles.taxQuotationBadge}>QUOTATION</Text>
            <Text style={styles.originalCopyText}>ORIGINAL FOR RECIPIENT</Text>
            <View style={styles.quotationMeta}>
              <View style={styles.metaRow}>
                <Text style={styles.metaLabel}>Quote No.</Text>
                <Text style={styles.metaValue}>{quotationInfo.number}</Text>
              </View>
              <View style={styles.metaRow}>
                <Text style={styles.metaLabel}>Issue Date</Text>
                <Text style={styles.metaValue}>{formatIndianDate(quotationInfo.issueDate)}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Three-column section */}
        <View style={styles.threeColumnSection}>
          <View style={styles.column}>
            <Text style={styles.sectionHeader}>Seller</Text>
            <View style={styles.detailItem}>
              <Text style={styles.detailLabel}>Name:</Text>
              <Text style={styles.detailValue}>{fromInfo?.company_name || fromInfo?.name || 'Company Name'}</Text>
            </View>
            {!!fromInfo?.address && (
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Address:</Text>
                <Text style={styles.detailValue}>{fromInfo.address}</Text>
              </View>
            )}
            {!!fromInfo?.phone && (
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Phone:</Text>
                <Text style={styles.detailValue}>{fromInfo.phone}</Text>
              </View>
            )}
            {!!(fromInfo?.contact_email || fromInfo?.email) && (
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Email:</Text>
                <Text style={styles.detailValue}>{fromInfo.contact_email || fromInfo.email}</Text>
              </View>
            )}
            {!!fromInfo?.gst_number && (
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>GSTIN:</Text>
                <Text style={styles.detailValue}>{fromInfo.gst_number}</Text>
              </View>
            )}
          </View>

          <View style={styles.column}>
            <Text style={styles.sectionHeader}>Buyer</Text>
            <View style={styles.detailItem}>
              <Text style={styles.detailLabel}>Name:</Text>
              <Text style={styles.detailValue}>
                {selectedClient ? selectedClient.client_name : newClient?.name || 'Customer Name'}
              </Text>
            </View>
            {!!(selectedClient ? selectedClient.client_company_name : newClient?.company) && (
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Company:</Text>
                <Text style={styles.detailValue}>
                  {selectedClient ? selectedClient.client_company_name : newClient.company}
                </Text>
              </View>
            )}
            {!!(selectedClient ? selectedClient.address : newClient?.address) && (
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Address:</Text>
                <Text style={styles.detailValue}>
                  {selectedClient ? selectedClient.address : newClient.address}
                </Text>
              </View>
            )}
            {!!(selectedClient ? selectedClient.email || selectedClient.client_email : newClient?.email) && (
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Email:</Text>
                <Text style={styles.detailValue}>
                  {selectedClient ? selectedClient.email || selectedClient.client_email : newClient.email}
                </Text>
              </View>
            )}
            <View style={styles.detailItem}>
              <Text style={styles.detailLabel}>GSTIN:</Text>
              <Text style={styles.detailValue}>
                {selectedClient ? selectedClient.client_gst_number : newClient?.gst || '—'}
              </Text>
            </View>
          </View>

          <View style={styles.lastColumn}>
            <Text style={styles.sectionHeader}>Quote Details</Text>
            <View style={styles.detailItem}>
              <Text style={styles.detailLabel}>Quote No.</Text>
              <Text style={styles.detailValue}>{quotationInfo.number}</Text>
            </View>
            <View style={styles.detailItem}>
              <Text style={styles.detailLabel}>Issue Date</Text>
              <Text style={styles.detailValue}>{formatIndianDate(quotationInfo.issueDate)}</Text>
            </View>
            <View style={styles.detailItem}>
              <Text style={styles.detailLabel}>Valid Until</Text>
              <Text style={styles.detailValue}>{formatIndianDate(quotationInfo.validUntil || quotationInfo.issueDate)}</Text>
            </View>
          </View>
        </View>

        {/* Items Table */}
        <ItemsTable rows={page1Items} />

        {/* Total row for page 1 items */}
        <View style={styles.table}>
          <View style={[styles.tableRow, styles.totalRow]}>
            <Text style={[styles.totalRowCell, { width: `${getTotalSpanWidth()}%` }]}>Total</Text>
            <Text style={[styles.totalRowCell, { width: `${columnWidths.total}%`, borderRight: 'none' }]}>
              Rs. {page1Items.reduce((sum, item) => {
                const b = getLocalItemTotal(item);
                const t = showTaxPerItemColumn ? calculateLocalItemTax(item) : 0;
                return sum + b + t;
              }, 0).toFixed(2)}
            </Text>
          </View>
        </View>

        {/* Render bottom blocks on page 1 if no second page */}
        {!hasSecondPage && <BottomBlocks />}
      </Page>

      {/* Page 2 if necessary */}
      {hasSecondPage && (
        <Page size="A4" style={styles.page}>
          <ItemsTable rows={remainingItems} showHeader={true} startIndex={FIRST_PAGE_MAX_ITEM_ROWS} />
          
          {/* Total row for remaining items */}
          <View style={styles.table}>
            <View style={[styles.tableRow, styles.totalRow]}>
              <Text style={[styles.totalRowCell, { width: `${getTotalSpanWidth()}%` }]}>Total</Text>
              <Text style={[styles.totalRowCell, { width: `${columnWidths.total}%`, borderRight: 'none' }]}>
                Rs. {remainingItems.reduce((sum, item) => {
                  const b = getLocalItemTotal(item);
                  const t = showTaxPerItemColumn ? calculateLocalItemTax(item) : 0;
                  return sum + b + t;
                }, 0).toFixed(2)}
              </Text>
            </View>
          </View>

          <BottomBlocks />
        </Page>
      )}
    </Document>
  );
});

QuotationPDF.displayName = 'QuotationPDF';

export default QuotationPDF;